import * as yup from 'yup';

import { ISelectorValue } from '../types/shell';
import { NO_DEPARTMENT } from './organizations';
import { URL_VARS } from './routing';
import { FALLBACK_CURRENCY_CODE } from './shell';

export const USER = 'user';
export const SUPER_ADMIN = 'super';
export const ORG_ADMIN = 'org';
export const DEPT_ADMIN = 'dept';
export const DISTRIBUTOR = 'dist';

export const ADDRESS1 = 'address1';
export const ADDRESS2 = 'address2';
export const CITY = 'city';
export const COUNTRY = 'country';
export const EMAIL = 'email';
export const FIRST_NAME = 'first_name';
export const LAST_NAME = 'last_name';
export const PHONE = 'phone';
export const STATE = 'state';
export const ZIP = 'zip';
export const DEPARTMENT_ID = 'department_id';
export const DISTRIBUTOR_ORG_IDS = 'distributor_org_ids';
export const ADMIN_TYPE = 'admin_type';
export const AVAILABLE_BUDGET = 'available_budget';
export const UID = 'uid';
export const CRM_SKIP_ALLOWED = 'crm_skip_allowed';
export const SKIP_WELCOME_EMAIL = 'skip_welcome_email';
export const DIGITAL_BALANCE_EMAILS_DISABLED = 'digital_balance_emails_disabled';
export const DISPLAY_CURRENCY = 'display_currency';
export const BUDGET_CURRENCY = 'budget_currency';

export enum UserFilterTypesEnum {
  All,
  Admin,
  User,
}

export const UserSuccessToastMessages = {
  [URL_VARS.NEW]: 'New user was added',
  [URL_VARS.EDIT]: 'User was edited',
};

const fieldNamesForErrorOutput = {
  [ADMIN_TYPE]: 'Admin type',
  [DEPARTMENT_ID]: 'Department',
  [DISTRIBUTOR_ORG_IDS]: 'Multi Org Admin org IDs',
  [EMAIL]: 'Email',
  [FIRST_NAME]: 'First name',
  [LAST_NAME]: 'Last name',
  [ADDRESS1]: 'Address 1',
  [ADDRESS2]: 'Address 2',
  [CITY]: 'City',
  [STATE]: 'State',
  [ZIP]: 'ZIP code',
  [COUNTRY]: 'Country',
  [PHONE]: 'Phone',
  [AVAILABLE_BUDGET]: 'Available budget',
};

export const USER_INITIAL_VALUES = {
  [ADMIN_TYPE]: '',
  [DEPARTMENT_ID]: NO_DEPARTMENT,
  [DISTRIBUTOR_ORG_IDS]: [] as string[],
  [EMAIL]: '',
  [FIRST_NAME]: '',
  [LAST_NAME]: '',
  [ADDRESS1]: '',
  [ADDRESS2]: '',
  [CITY]: '',
  [STATE]: '',
  [ZIP]: '',
  [COUNTRY]: '',
  [PHONE]: '',
  [AVAILABLE_BUDGET]: 0,
  [CRM_SKIP_ALLOWED]: false,
  [SKIP_WELCOME_EMAIL]: false,
  [DISPLAY_CURRENCY]: FALLBACK_CURRENCY_CODE,
  [BUDGET_CURRENCY]: FALLBACK_CURRENCY_CODE,
};

const USERS_VALIDATION_SCHEMA_BASE = {
  [ADMIN_TYPE]: yup.string().label(fieldNamesForErrorOutput[ADMIN_TYPE]).required(),
  [DEPARTMENT_ID]: yup.string().label(fieldNamesForErrorOutput[DEPARTMENT_ID]).required(),
  [DISTRIBUTOR_ORG_IDS]: yup.array().when(ADMIN_TYPE, {
    is: DISTRIBUTOR,
    then: (sc) =>
      sc
        .of(yup.string().required())
        .min(1, 'This field must have at least 1 value')
        .label(fieldNamesForErrorOutput[DISTRIBUTOR_ORG_IDS]),
    otherwise: (sc) => sc.notRequired(),
  }),
  [EMAIL]: yup.string().email().label(fieldNamesForErrorOutput[EMAIL]).required(),
  [FIRST_NAME]: yup.string().label(fieldNamesForErrorOutput[FIRST_NAME]).required(),
  [LAST_NAME]: yup.string().label(fieldNamesForErrorOutput[LAST_NAME]).required(),
  [ADDRESS1]: yup.string().label(fieldNamesForErrorOutput[ADDRESS1]),
  [ADDRESS2]: yup.string(),
  [CITY]: yup.string().label(fieldNamesForErrorOutput[CITY]),
  [STATE]: yup.string().label(fieldNamesForErrorOutput[STATE]),
  [ZIP]: yup.string().label(fieldNamesForErrorOutput[ZIP]),
  [COUNTRY]: yup.string().label(fieldNamesForErrorOutput[COUNTRY]),
  [PHONE]: yup.string().label(fieldNamesForErrorOutput[PHONE]),
  [CRM_SKIP_ALLOWED]: yup.boolean(),
  [SKIP_WELCOME_EMAIL]: yup.boolean(),
  [DISPLAY_CURRENCY]: yup.string(),
  [BUDGET_CURRENCY]: yup.string().required(),
};

export const USERS_VALIDATION_SCHEMA = yup.object().shape({
  ...USERS_VALIDATION_SCHEMA_BASE,
  [AVAILABLE_BUDGET]: yup.number().moreThan(0).label(fieldNamesForErrorOutput[AVAILABLE_BUDGET]).required(),
});

export const USERS_VALIDATION_SCHEMA_NO_BUDGET = yup.object().shape(USERS_VALIDATION_SCHEMA_BASE);

export const ROLES_SELECT_OPTIONS = (role?: string): ISelectorValue[] => {
  const roles = [
    {
      label: 'User',
      value: USER,
    },
    {
      label: 'Department admin',
      value: DEPT_ADMIN,
    },
    {
      label: 'Organization admin',
      value: ORG_ADMIN,
    },
    {
      label: 'Multi Org Admin',
      value: DISTRIBUTOR,
    },
  ];

  if (role === SUPER_ADMIN) {
    roles.push({
      label: 'Super admin',
      value: SUPER_ADMIN,
    });
  }

  return roles;
};

export const USERS_EDITING_PERMISSION_LIST: Record<string, string[]> = {
  [SUPER_ADMIN]: [SUPER_ADMIN, DISTRIBUTOR, ORG_ADMIN, DEPT_ADMIN, USER, ''],
  [DISTRIBUTOR]: [ORG_ADMIN, DEPT_ADMIN, USER, ''],
  [ORG_ADMIN]: [ORG_ADMIN, DEPT_ADMIN, USER, ''],
};

export const USER_REASSIGN_REQUIRED_ERROR_CODE = 'UserReassignRequired';

export const DISPLAY_CURRENCY_HINT =
  "All prices in the portal for this user will be displayed in the selected currency.To view prices in the original currency, choose 'None'.";

export const COLUMN_IDS = {
  USER: 'user',
  INTEGRATIONS: 'integrations',
  ROLE: 'role',
  DEPARTMENT: 'department',
  BUDGET_LEFT: 'budget_left',
  BUDGET_SPENT: 'budget_spent',
  BUDGET_TOTAL: 'budget_total',
  LAST_LOGIN: 'last_login',
  ACTIONS: 'actions',
  PRODUCTION_TYPE: 'production_type',
};

export const VISIBLE_USERS_COLS_INITIAL_STATE = {
  [COLUMN_IDS.USER]: true,
  [COLUMN_IDS.INTEGRATIONS]: true,
  [COLUMN_IDS.ROLE]: true,
  [COLUMN_IDS.DEPARTMENT]: true,
  [COLUMN_IDS.BUDGET_LEFT]: true,
  [COLUMN_IDS.BUDGET_SPENT]: false,
  [COLUMN_IDS.BUDGET_TOTAL]: true,
  [COLUMN_IDS.LAST_LOGIN]: true,
  [COLUMN_IDS.ACTIONS]: true,
  [COLUMN_IDS.PRODUCTION_TYPE]: false,
};

export const USER_ROLE_FILTER_OPTIONS = [
  { label: 'User', value: USER },
  { label: 'Admin', value: ORG_ADMIN },
  { label: 'Super', value: SUPER_ADMIN },
  { label: 'Department', value: DEPT_ADMIN },
  { label: 'Multi Org Admin', value: DISTRIBUTOR },
];

export const ADMIN_ROLES = [DEPT_ADMIN, ORG_ADMIN, DISTRIBUTOR, SUPER_ADMIN];
export const HUBSPOT_APP_INSTALL_ROLES = [ORG_ADMIN, DISTRIBUTOR, SUPER_ADMIN];
